


















import { computed, defineComponent, onBeforeUnmount, onMounted, provide, ref, watch } from "@vue/composition-api";
import { InputMessageSearch, Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import { PaymentPackage, PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import usePager from "@/hooks/usePager";
import usePaymentPackage from "@/hooks/usePaymentPackage";
import providers from "@/lib/api";
import appStorage from "@/lib/appStorage";
import emitter from "@/lib/emitter";
import { EVENTS } from "@/const";
import MessagesView from "../PageMessages/components/MessagesView.vue";
import useConvertingFiles from "../PageMessages/hooks/useConvertingFiles";
import useMessageThread from "../PageMessages/hooks/useMessageThread";

export default defineComponent({
  name: "PageMessages",
  components: { MessagesView },
  props: {
    ownerId: { type: String, required: true },
    threadId: { type: String, required: true },
  },
  setup(props, { root }) {
    const { authUser, authorities } = useAuthUser();
    const { findAll: findAllPaymentPackages } = usePaymentPackage();

    const { messageThread, opponentMinUnreadMessageId, opponent, getThreadById } = useMessageThread(
      computed(() => ({
        ownerId: props.ownerId,
        threadId: props.threadId,
      }))
    );

    const loading = ref(false);
    const paymentPackages = ref<PaymentPackage[]>([]);
    const messagesViewRef = ref();

    const providedData = ref<{ user?: UserPublicData }>({});

    const {
      enableScroll,
      data: messagesSearchResult,
      items,
      getNextPage: getPrevMessages,
      getFirstPage: getMessages,
      updateItem,
    } = usePager<Message, InputMessageSearch>(async (page, requestData) => {
      return providers.MessagesProvider.find({
        data: {
          threadId: props.threadId,
          sort: "TIME,DESC",
          page,
          ...requestData,
        },
      });
    });
    const messageList = computed(() => Array.from<Message>(items.value ?? []).reverse());

    useConvertingFiles(messageList, updateItem);

    const getPaymentPackages = async () => {
      paymentPackages.value = await findAllPaymentPackages([PaymentPackageEnum.MESSAGE_ATTACHMENT]);
    };

    const checkAccess = async () => {
      if (!checkAuthorities(authorities.value, ["getAnyMessage"])) {
        const accessToken = appStorage.getString("admin_LONG_LIVE_TOKEN", "");

        if (!accessToken) {
          await root.$router.push({ name: "messageThreads" });
        } else {
          const currentToken = await providers.LoginProvider.getLongLiveToken();
          if (currentToken !== accessToken) {
            await providers.LoginProvider.refreshTokenV2({ token: accessToken });
          }
        }
      }
    };

    const getData = async () => {
      try {
        loading.value = true;
        await checkAccess();
        if (!authUser.value) return;
        const promises: Promise<any>[] = [getPaymentPackages(), getThreadById(props.threadId), getMessages()];
        await Promise.all(promises);
        if (!messageThread.value) {
          root.$router.push({ name: "messageThreads" });
        }
      } finally {
        loading.value = false;
      }
    };

    provide("messageData", providedData.value);

    watch(opponent, () => {
      providedData.value.user = opponent.value || undefined;
    });

    watch(items, async (newVal, oldVal) => {
      const ids = newVal
        .slice(oldVal.length)
        .filter(i => i.attachmentPricePackageId !== "0")
        .map(i => i.id);
      const response = await providers.ContentPriceProvider.searchPurchases({
        data: { subjects: ids.map(id => ({ subjectId: id, subjectType: "message_attachment" })) },
      });

      response.result.forEach(i => {
        const messageToChange = messagesSearchResult.value?.result.find(m => m.id === i.subject.subjectId);
        messageToChange && (messageToChange.attachmentPurchasers = [i.userId]);
      });
    });

    getData();

    onMounted(() => {
      emitter.emit(EVENTS.MAIN_MENU_HIDE, true);
    });

    onBeforeUnmount(() => {
      emitter.emit(EVENTS.MAIN_MENU_HIDE, false);
    });

    return {
      messagesViewRef,
      loading,
      messageThread,
      messageList,
      opponentMinUnreadMessageId,
      opponent,
      enableScroll,
      paymentPackages,
      getPrevMessages,
    };
  },
});
